import { DiscordSDK, patchUrlMappings } from '@discord/embedded-app-sdk';

const CLIENT_ID = process.env.REACT_APP_DISCORD_CLIENT_ID || '';

export function getSiteOrigin(): string {
    return (process.env.REACT_APP_SITE_URL || 'https://empiremaps.live').replace(/\/$/, '');
}

/** Resolve static asset paths for Discord's sandbox (root / mapping proxies to empiremaps.live). */
export function resolveSiteAssetUrl(path: string): string {
    if (!path.startsWith('/')) {
        try {
            const url = new URL(path);
            const siteHost = new URL(getSiteOrigin()).host;
            if (url.host === siteHost) {
                return url.pathname + url.search;
            }
            if (isDiscordEmbedded() && url.host === 'a.espncdn.com') {
                return `/espn-cdn${url.pathname}${url.search}`;
            }
        } catch {
            /* not a URL */
        }
        return path;
    }

    // Relative paths (/logos/...) are proxied via the root URL mapping to empiremaps.live.
    return path;
}

/**
 * Must match URL Mappings in the Discord Developer Portal (Activities → URL Mappings).
 * Own-map Activity needs API + site + ESPN logos. Mapbox prefixes are legacy (old Activity panel).
 */
function buildDiscordProxyMappings() {
    const mappings = [
        { prefix: '/espn-cdn', target: 'a.espncdn.com' },
        // Legacy Mapbox Activity — harmless if unused by own-map
        { prefix: '/mapbox-api', target: 'api.mapbox.com' },
        { prefix: '/mapbox-events', target: 'events.mapbox.com' },
        { prefix: '/mapbox-tiles/{subdomain}', target: '{subdomain}.tiles.mapbox.com' },
    ];

    try {
        const siteHost = new URL(getSiteOrigin()).host;
        mappings.unshift({ prefix: '/empire-site', target: siteHost });
    } catch {
        mappings.unshift({ prefix: '/empire-site', target: 'empiremaps.live' });
    }

    // Root mapping (/) also proxies to empiremaps.live — /logos/... and geo assets via site host.

    const apiUrl = process.env.REACT_APP_API_URL || '';
    try {
        const parsed = new URL(apiUrl);
        const target = `${parsed.host}${parsed.pathname.replace(/\/$/, '')}`;
        mappings.unshift({ prefix: '/empire-api', target });
    } catch {
        mappings.unshift({ prefix: '/empire-api', target: 'empiremaps-api.onrender.com/api/v2' });
    }

    return mappings;
}

let proxyMappingsApplied = false;

export function setupDiscordProxyMappings(): void {
    if (!isDiscordEmbedded() || proxyMappingsApplied) return;

    patchUrlMappings(buildDiscordProxyMappings(), {
        patchFetch: true,
        patchWebSocket: true,
        patchXhr: true,
        patchSrcAttributes: true,
    });
    proxyMappingsApplied = true;
}

export function isDiscordEmbedded(): boolean {
    if (typeof window === 'undefined') return false;

    const params = new URLSearchParams(window.location.search);
    if (params.has('frame_id') || params.has('instance_id') || params.has('launch_id')) {
        return true;
    }

    try {
        if (window.location.hostname.includes('discordsays.com')) return true;
    } catch {
        /* ignore */
    }

    return window.parent !== window;
}

export function getDiscordClientId(): string {
    return CLIENT_ID;
}

/** API base that routes through Discord URL mappings when embedded. */
function discordApiBase(): string {
    if (isDiscordEmbedded()) return '/empire-api';
    return process.env.REACT_APP_API_URL || '/api/v2';
}

let sdkInstance: DiscordSDK | null = null;
let sdkReadyPromise: Promise<DiscordSDK | null> | null = null;

/**
 * Connect to the Discord client. Only requires `ready()` — no OAuth.
 * Needed for openExternalLink and other sandboxed commands.
 */
export async function ensureDiscordSdk(): Promise<DiscordSDK | null> {
    if (!CLIENT_ID || !isDiscordEmbedded()) return null;
    if (sdkInstance) return sdkInstance;
    if (!sdkReadyPromise) {
        sdkReadyPromise = (async () => {
            setupDiscordProxyMappings();
            const sdk = new DiscordSDK(CLIENT_ID);
            await sdk.ready();
            sdkInstance = sdk;
            return sdk;
        })().catch((err) => {
            console.warn('[discord] SDK ready failed:', err);
            sdkReadyPromise = null;
            return null;
        });
    }
    return sdkReadyPromise;
}

export interface DiscordSession {
    sdk: DiscordSDK;
    user: { id: string; username: string; global_name?: string | null };
}

export async function authenticateDiscordUser(
    sdk: DiscordSDK,
): Promise<DiscordSession['user']> {
    const { code } = await sdk.commands.authorize({
        client_id: CLIENT_ID,
        response_type: 'code',
        state: '',
        prompt: 'none',
        scope: ['identify'],
    });

    const tokenRes = await fetch(`${discordApiBase()}/discord/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
    });

    const tokenJson = await tokenRes.json();
    if (!tokenRes.ok || !tokenJson.access_token) {
        throw new Error(tokenJson.error || 'Discord authentication failed');
    }

    const auth = await sdk.commands.authenticate({ access_token: tokenJson.access_token });

    return {
        id: auth.user.id,
        username: auth.user.username,
        global_name: auth.user.global_name,
    };
}

/**
 * Initialize the Discord Embedded App SDK and authenticate the user.
 * Resolves null when not running inside Discord or when client id is missing.
 */
export async function initDiscordSession(): Promise<DiscordSession | null> {
    if (!CLIENT_ID) {
        console.warn('[discord] REACT_APP_DISCORD_CLIENT_ID not set');
        return null;
    }

    const sdk = await ensureDiscordSdk();
    if (!sdk) return null;

    const user = await authenticateDiscordUser(sdk);
    return { sdk, user };
}

export async function setDiscordActivity(
    sdk: DiscordSDK,
    leagueId: string,
    leagueName: string,
    leaderName?: string
): Promise<void> {
    try {
        await sdk.commands.setActivity({
            activity: {
                type: 0,
                details: leaderName ? `${leaderName} leads the map` : 'Explore the live conquest map',
                state: leagueName,
            },
        });
    } catch (error) {
        console.warn('[discord] setActivity failed:', error);
    }
}

export async function openExternalMap(
    sdk: DiscordSDK | null,
    leagueId: string,
    options?: { teamId?: number; date?: string | null },
): Promise<void> {
    const params = new URLSearchParams();
    if (options?.teamId != null) params.set('focusTeam', String(options.teamId));
    if (options?.date) params.set('date', options.date);
    const qs = params.toString();
    // Product home is the own-map; Mapbox leagues remain at /league/:id for attic.
    const url =
        leagueId === 'ncaa_basketball'
            ? `${getSiteOrigin()}/${qs ? `?${qs}` : ''}`
            : `${getSiteOrigin()}/league/${leagueId}${qs ? `?${qs}` : ''}`;

    const activeSdk = sdk ?? (isDiscordEmbedded() ? await ensureDiscordSdk() : null);
    if (activeSdk) {
        try {
            await activeSdk.commands.openExternalLink({ url });
            return;
        } catch (err) {
            console.warn('[discord] openExternalLink failed:', err);
        }
    }

    if (!isDiscordEmbedded()) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
}
